import { Alert, Snackbar } from '@mui/material';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import './index.scss';

interface ToastContextType {
  showToast: (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  /*
   * Stable on purpose. Screens list showToast in their fetch callbacks' deps, so a new function
   * per render meant a failed load toasted, the toast re-rendered this provider, the fetch re-ran
   * and failed again, and the 3s auto-close fired one more round: Drip Enrollments retried every
   * 3s for as long as its API failed. Email Campaigns mounts all seven tabs at once, so any toast
   * there also refetched every other tab that depends on it.
   */
  const showToast = useCallback((msg: string, sev: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const handleClose = () => setOpen(false);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={3000} open={open} onClose={handleClose}>
        <Alert className="toast-alert" severity={severity} variant="filled" onClose={handleClose}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
